import { useTable, Column, Row, HeaderGroup } from 'react-table'
import clsx from 'clsx'
import Button from '../buttons/button'
import ContentTile from '../content/ContentTile'
import TypographyParagraph from '../typography/TypographyParagraph'
import {
	ArrowDownIcon,
	ArrowUpIcon,
	CalloutSuccessIcon,
	CountersignIcon,
	MoreVerticalIcon,
	RevokeIcon,
	SignIcon,
} from '../icons'
import Menu from '../timeline/menu'
import { useMemo } from 'react'
import {
	getCollectionPreviewURL,
	getCollectionProfileURL,
	getMetadataProfileURL,
	getNFTProfileURL,
	getUserProfileURL,
	mapStatus,
	truncateStringInMiddle,
} from '#app/utils/misc.tsx'
import InternalLink from '../common/internalLink'
import VideoPlaceholder from '#images/video-placeholder.jpg'
import ImagePlaceholder from '#images/image-placeholder.jpg'
import walletIcon from '#images/wallet-icon.png'
import walletIconDark from '#images/wallet-icon-dark.png'
import { useTheme } from '#app/utils/providers/themeProvider'
import type { Theme } from '#app/utils/providers/themeProvider'
import { AttestationType } from '#types/loreTypes.tsx'

interface MetadataEntry extends Partial<AttestationType> {
	references: {
		attestationIPFSCID?: string
		metadataIPFSCID?: string
		entry?: {
			chain?: any
			targetWallet?: any
		}
		chain?: any
		targetWallet?: any
	}
	properties: {
		artistInitiated?: boolean
		receiverAddress?: string
		ownerAddress?: string
	}
	template: {
		name: string
		slug: string
	}
	signature?: string
	address?: string
	isRevoked?: boolean
	nftName?: string
	nftThumbnail?: string
	nftContentType?: string
	nftCollection?: {
		name: string
	}
	targetWalletAddress?: string
	originalReference?: {
		template: {
			name: string
		}
	}
	createdAt: string
	comparisonDate?: string
}

interface RowProps {
	row: MetadataEntry
	displayCountersignType?: boolean
}

interface SignatureRowProps {
	row: MetadataEntry
	onPendingSign: (row: MetadataEntry) => void
}

interface CounterSignatureRowProps {
	row: MetadataEntry
	walletAddress: string
	onCountersign: (row: MetadataEntry) => void
}

interface NFTRowProps {
	row: {
		original: MetadataEntry
	}
	currentTheme: Theme
}

interface MenuRowProps {
	row: MetadataEntry
	walletAddress: string
	onPendingSign: (row: MetadataEntry) => void
	onDelete: (row: MetadataEntry) => void
	onCountersign: (row: MetadataEntry) => void
	onRevoke: (row: MetadataEntry) => void
	atomicLoreUrl: string
}

interface DataTableProps {
	data: MetadataEntry[]
	showFilter?: boolean
	showButton?: boolean
	sortRow?: string
	showStats?: boolean
	walletId?: string | null
	entryTitle?: string
	isLoading?: boolean
	walletAddress: string
	onPendingSign: (row: MetadataEntry) => void
	onDelete: (row: MetadataEntry) => void
	onCountersign: (row: MetadataEntry) => void
	onRevoke: (row: MetadataEntry) => void
	atomicLoreUrl: string
	displayCountersignType?: boolean
}

interface ExtendedHeaderGroup<T extends object> extends HeaderGroup<T> {
	isSorted?: boolean
	isSortedDesc?: boolean
}

const canCountersign = (metadataEntry, walletAddress) => {
	return (
		metadataEntry.references.attestationIPFSCID &&
		metadataEntry.properties &&
		walletAddress &&
		!metadataEntry.isReattested &&
		(((metadataEntry.properties.artistInitiated ||
			metadataEntry.properties.artistInitiated === undefined) &&
			metadataEntry.properties.receiverAddress &&
			metadataEntry.properties.receiverAddress.toLowerCase() ==
				walletAddress.toLowerCase()) ||
			(metadataEntry.properties.artistInitiated === false &&
				metadataEntry.properties.ownerAddress &&
				metadataEntry.properties.ownerAddress.toLowerCase() ==
					walletAddress.toLowerCase()))
	)
}

const StatusRow = ({ row, displayCountersignType }) => {
	if (displayCountersignType) {
		return mapStatus(row) == 'Pending' ? 'Yes' : 'No'
	}
	if (!row.signature)
		return (
			<div className="ml-1 flex flex-row gap-x-2">
				<div className="text-textSecondaryDark">
					<SignIcon />
				</div>
				Draft
			</div>
		)
	if (row.isRevoked)
		return (
			<div className="flex flex-row gap-x-2">
				<div className="text-[#FF6F6F]">
					<RevokeIcon />
				</div>
				Revoked
			</div>
		)
	if (mapStatus(row) == 'Pending')
		return (
			<div className="ml-1 flex flex-row gap-x-2">
				<div className="text-textSecondaryDark">
					<SignIcon />
				</div>
				{mapStatus(row)}
			</div>
		)
	if (mapStatus(row) == 'Accepted')
		return (
			<div className="flex flex-row gap-x-2">
				<div className="text-[#33DF64]">
					<CountersignIcon />
				</div>
				{mapStatus(row)}
			</div>
		)
	return (
		<div className="flex flex-row gap-x-2">
			<div className="text-[#33DF64]">
				<CalloutSuccessIcon />
			</div>
			Completed
		</div>
	)
}

const SignatureRow = ({ row, onPendingSign }) => {
	if (row.signature) return truncateStringInMiddle(row.address)
	return (
		<Button
			variant="secondary"
			size="small"
			additionalClasses="sm:w-full"
			onClick={() => onPendingSign(row)}
		>
			{row.template.slug === 'collection' ? 'Edit' : 'Sign'}
		</Button>
	)
}

const CounterSignatureRow = ({ row, walletAddress, onCountersign }) => {
	const countersigningAddress =
		(row.properties.artistInitiated ||
			row.properties.artistInitiated === undefined) &&
		row.properties.receiverAddress
			? truncateStringInMiddle(row.properties.receiverAddress)
			: row.properties.artistInitiated === false && row.properties.ownerAddress
				? truncateStringInMiddle(row.properties.ownerAddress)
				: ''
	if (canCountersign(row, walletAddress)) {
		return (
			<Button
				variant="secondary"
				size="small"
				additionalClasses="sm:w-full"
				onClick={() => onCountersign(row)}
			>
				Countersign
			</Button>
		)
	}
	return countersigningAddress
}

const NFTRow = ({ row, currentTheme }: NFTRowProps) => {
	const attestation = row.original

	const imageUrl = useMemo(() => {
		if (
			attestation.references.targetWallet ||
			attestation.references.entry?.targetWallet
		) {
			return currentTheme === 'dark' ? walletIconDark : walletIcon
		} else {
			return attestation.nftThumbnail
		}
	}, [currentTheme, attestation])

	const getTitle = () => {
		if (attestation.template.slug === 'revoke') {
			return `Revoked ${attestation.originalReference?.template.name}`
		} else if (attestation.nftName) {
			return attestation.nftName
		} else if (attestation.targetWalletAddress) {
			return truncateStringInMiddle(attestation.targetWalletAddress)
		} else {
			return attestation.template.name
		}
	}

	const cellTitle = getTitle()
	return (
		<div className="flex cursor-pointer items-center gap-x-4 py-0 pl-0 pr-2 hover:underline">
			{attestation.nftName &&
			(attestation.references.entry?.chain || attestation.references.chain) ? (
				<InternalLink to={getNFTProfileURL(attestation) || ''} prefetch="none">
					<ContentTile
						variant="collection"
						title={cellTitle}
						description={attestation.nftCollection?.name || ''}
						image={imageUrl || ''}
						fallbackImage={
							attestation.nftContentType?.includes('video')
								? VideoPlaceholder
								: ImagePlaceholder
						}
						isVerified={false}
						showBorder={false}
						showCaret={false}
					/>
				</InternalLink>
			) : attestation.template &&
			  attestation.template &&
			  attestation.template.slug === 'collection' &&
			  !attestation.references.attestationIPFSCID ? (
				<InternalLink
					to={getCollectionPreviewURL(attestation) || ''}
					prefetch="none"
				>
					<ContentTile
						variant="collection"
						title={cellTitle}
						description={''}
						image={imageUrl || ''}
						isVerified={false}
						showBorder={false}
						showCaret={false}
					/>
				</InternalLink>
			) : attestation.originalReference &&
			  attestation.originalReference.template &&
			  attestation.originalReference.template.slug === 'collection' ? (
				<InternalLink
					to={getCollectionProfileURL(attestation) || ''}
					prefetch="none"
				>
					<ContentTile
						variant="collection"
						title={cellTitle}
						description={''}
						image={imageUrl || ''}
						isVerified={false}
						showBorder={false}
						showCaret={false}
					/>
				</InternalLink>
			) : attestation.targetWalletAddress &&
			  attestation.user &&
			  attestation.user.id ? (
				<InternalLink
					to={getUserProfileURL(attestation.user.id) || ''}
					prefetch="none"
				>
					<ContentTile
						variant="collection"
						title={cellTitle}
						description={''}
						image={imageUrl || ''}
						isVerified={false}
						showBorder={false}
						showCaret={false}
					/>
				</InternalLink>
			) : attestation.template && attestation.references.attestationIPFSCID ? (
				<InternalLink
					to={getMetadataProfileURL(attestation) || ''}
					prefetch="none"
				>
					<ContentTile
						variant="collection"
						title={cellTitle}
						description={''}
						image={imageUrl || ''}
						isVerified={false}
						showBorder={false}
						showCaret={false}
					/>
				</InternalLink>
			) : attestation.template ? (
				<ContentTile
					variant="collection"
					title={cellTitle}
					description={''}
					image={imageUrl || ''}
					isVerified={false}
					showBorder={false}
					showCaret={false}
				/>
			) : (
				<TypographyParagraph
					size="medium"
					additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
				>
					Loading...
				</TypographyParagraph>
			)}
		</div>
	)
}

const MenuRow = ({
	row,
	walletAddress,
	onPendingSign,
	onDelete,
	onCountersign,
	onRevoke,
	atomicLoreUrl,
}) => {
	return (
		<Menu
			menuButton={<MoreVerticalIcon />}
			metadataEntry={row}
			metadataType={row.template.name}
			walletAddress={walletAddress}
			metadataProfileUrl={getMetadataProfileURL(row, atomicLoreUrl)}
			onPendingSign={() => onPendingSign(row)}
			onDelete={() => onDelete(row)}
			onCountersign={() => onCountersign(row)}
			onRevoke={() => onRevoke(row)}
		/>
	)
}

export default function DataTable({
	data,
	showFilter = true,
	showButton = true,
	sortRow = 'date',
	showStats = false,
	walletId = null,
	entryTitle = 'NFT',
	isLoading = false,
	walletAddress,
	onPendingSign,
	onDelete,
	onCountersign,
	onRevoke,
	atomicLoreUrl,
	displayCountersignType = false,
}: DataTableProps) {
	const [theme] = useTheme()

	const columns = useMemo<Column<MetadataEntry>[]>(
		() => [
			{
				Header: 'NFT/Collection',
				id: 'nft',
				Cell: ({ row }) => <NFTRow row={row} currentTheme={theme || 'light'} />,
			},
			{
				Header: 'Date',
				id: 'date',
				accessor: (row: MetadataEntry) => {
					const comparisonDate = row.comparisonDate || row.createdAt
					return new Date(comparisonDate).toLocaleDateString()
				},
			},
			{
				Header: 'Signing Address',
				id: 'signingAddress',
				accessor: (row: MetadataEntry) => (
					<SignatureRow row={row} onPendingSign={onPendingSign} />
				),
			},
			{
				Header: 'Co-Signing Address',
				id: 'cosigningAddress',
				accessor: (row: MetadataEntry) => (
					<CounterSignatureRow
						row={row}
						walletAddress={walletAddress}
						onCountersign={onCountersign}
					/>
				),
			},
			{
				Header: 'Template',
				id: 'template',
				accessor: (row: MetadataEntry) =>
					row.template ? row.template.name : '',
			},
			{
				Header: displayCountersignType ? 'Countersign required' : 'Status',
				id: 'status',
				accessor: (row: MetadataEntry) => (
					<StatusRow
						row={row}
						displayCountersignType={displayCountersignType}
					/>
				),
			},
			{
				Header: '',
				id: 'menu',
				accessor: (row: MetadataEntry) => (
					<MenuRow
						row={row}
						walletAddress={walletAddress}
						onPendingSign={onPendingSign}
						onDelete={onDelete}
						onCountersign={onCountersign}
						onRevoke={onRevoke}
						atomicLoreUrl={atomicLoreUrl}
					/>
				),
			},
		],
		[
			walletAddress,
			onPendingSign,
			onDelete,
			onCountersign,
			onRevoke,
			atomicLoreUrl,
			displayCountersignType,
			theme,
		],
	)

	const tableData = useMemo(() => data, [data])

	const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
		useTable({
			columns,
			data: tableData,
			autoResetHiddenColumns: false,
		})

	return (
		<div className="overflow-x-auto">
			<table {...getTableProps()} className="min-w-full table-auto">
				<thead className="py-6">
					{headerGroups.map((headerGroup, index) => (
						<tr
							key={`headerGroups-${index}`}
							className="font-abc text-paragraphSmall leading-6 text-textPrimaryLight dark:text-textPrimaryDark"
						>
							{headerGroup.headers.map((column, index) => {
								return (
									<th
										{...column.getHeaderProps()}
										key={`headerGroupHeaders-${index}`}
										scope="col"
										className={clsx(
											column.isSorted && 'underline',
											column.Header === 'NFT/Collection' &&
												'min-w-[100px] pl-4 md:table-cell',
											column.Header === 'Date' && 'min-w-[120px] md:table-cell',
											column.Header === 'Signing Address' &&
												'min-w-[120px] pr-2 md:table-cell',
											column.Header === 'Co-Signing Address' &&
												'min-w-[120px] pr-2 md:table-cell',
											column.Header === 'Template' &&
												'min-w-[120px] md:table-cell',
											(column.Header === 'Status' ||
												column.Header === 'Countersign required') &&
												'min-w-[120px] md:table-cell',
											column.Header === '' && 'min-w-[60px] md:table-cell',
											'relative h-10 py-0 pl-0 pr-2 text-left align-text-bottom font-normal',
										)}
									>
										{column.render('Header')}
										<span className="absolute top-[5px]">
											{column.isSorted ? (
												column.isSortedDesc ? (
													<ArrowDownIcon className="ml-[2px] h-3 w-3" />
												) : (
													<ArrowUpIcon className="ml-[2px] h-3 w-3" />
												)
											) : (
												''
											)}
										</span>
									</th>
								)
							})}
						</tr>
					))}
				</thead>
				<tbody {...getTableBodyProps()}>
					{rows.map((row, index) => {
						prepareRow(row)
						return (
							<tr
								key={`${row.original.references.metadataIPFSCID}-${index}`}
								className={clsx(
									'h-[72px] text-sm leading-6 text-textPrimaryLight hover:bg-grey100 dark:text-textPrimaryDark dark:hover:bg-[#161616]',
								)}
							>
								{row.cells.map((cell, index) => {
									return (
										<td
											key={`${row.original.references.metadataIPFSCID}-${cell.column.id}-${index}`}
											className={clsx(
												cell.column.Header === 'NFT/Collection' &&
													'max-w-[300px] pl-2 md:table-cell',
												cell.column.Header === 'Date' && 'md:table-cell',
												cell.column.Header === 'Signing Address' &&
													'pr-2 md:table-cell',
												cell.column.Header === 'Co-Signing Address' &&
													'pr-2 md:table-cell',
												cell.column.Header === 'Template' && 'md:table-cell',
												(cell.column.Header === 'Status' ||
													cell.column.Header === 'Countersign required') &&
													'md:table-cell',
												cell.column.Header === '' && 'pl-6 md:table-cell',
												'whitespace-no-wrap px-0 py-0 text-left',
											)}
										>
											{cell.render('Cell', {
												walletAddress,
												onPendingSign,
												onDelete,
												onCountersign,
												onRevoke,
												atomicLoreUrl,
												displayCountersignType,
											})}
										</td>
									)
								})}
							</tr>
						)
					})}
				</tbody>
			</table>
		</div>
	)
}
